import { CouponPricing, LocationState } from '@shared/types';
import { formatPrice } from '@shared/utils';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useI18n } from 'src/i18n/i18nContext';
import { couponCodeAPI } from 'src/services/api';
import { useBookingCartStore } from 'src/stores/bookingCartStore';
import { useServiceMetaStore } from 'src/stores/serviceMetaStore';

type Props = {
  couponCode: string;
  setCouponCode: React.Dispatch<React.SetStateAction<string>>;
  appliedCoupon: boolean;
  setAppliedCoupon: React.Dispatch<React.SetStateAction<boolean>>;
  couponPricing: CouponPricing | null;
  setCouponPricing: React.Dispatch<React.SetStateAction<CouponPricing | null>>;
  getValidationEmail: () => string;
  onRemoveCoupon: () => void;
  pricingBreakdown: {
    totalPrice: number;
    effectivePrice: number;
  };
};

const CouponCode: React.FC<Props> = (props) => {
  const {
    appliedCoupon,
    setAppliedCoupon,
    setCouponCode,
    couponCode,
    couponPricing,
    setCouponPricing,
    getValidationEmail,
    onRemoveCoupon,
    pricingBreakdown,
  } = props;

  const [couponError, setCouponError] = useState<string | null>(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponMessage, setCouponMessage] = useState<string | null>(null);

  const { totalPrice, effectivePrice } = pricingBreakdown;

  const { items: cartItems } = useBookingCartStore();
  const { credits: serviceCredits } = useServiceMetaStore();
  const location = useLocation();
  const state = location.state as LocationState | undefined;

  const { t, language } = useI18n();

  // Coupon code handlers
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setCouponError(t.coupon?.errors?.missingCode);
      return;
    }

    // Determine email for validation
    const validationEmail = getValidationEmail();
    if (!validationEmail) {
      setCouponError(t.coupon?.errors?.missingEmail);
      return;
    }

    setApplyingCoupon(true);
    setCouponError(null);

    try {
      // Get total credits required and prices from cart
      // Combined services like Contrast Therapy require multiple credits per booking
      let sessionCount = 0;
      const cartPrices: number[] = [];

      if (cartItems.length > 0) {
        for (const item of cartItems) {
          const credits = serviceCredits[item.id] || 1;
          const priceInCents = Math.round((item.price || 0) * 100);
          for (let i = 0; i < credits; i++) {
            cartPrices.push(Math.round(priceInCents / credits));
          }
          sessionCount += credits;
        }
      } else if (state) {
        const credits = serviceCredits[state.service.id] || 1;
        const priceInCents = state.service.price ? Math.round(state.service.price * 100) : 0;
        for (let i = 0; i < credits; i++) {
          cartPrices.push(Math.round(priceInCents / credits));
        }
        sessionCount = credits;
      }

      const response = await couponCodeAPI.validate(
        couponCode.trim().toUpperCase(),
        validationEmail,
        sessionCount,
        language,
        cartPrices
      );

      const result = response.data;

      if (result.valid) {
        setAppliedCoupon(true);
        setCouponPricing({
          originalPrice: result.pricing.originalPrice,
          couponPrice: result.pricing.couponPrice,
          discount: result.pricing.discount,
          sessionsIncluded: result.pricing.sessionsIncluded,
          sessionsToApplyDiscount: result.pricing.sessionsToApplyDiscount,
          sessionsAlreadyUsed: result.pricing.sessionsAlreadyUsed,
          sessionsRemainingOnCoupon: result.pricing.sessionsRemainingOnCoupon,
        });
        setCouponMessage(result.message);
        setCouponError(null);
      } else {
        setCouponError(
          result.message || t.coupon?.errors?.validationError || 'Invalid coupon code.'
        );
        setAppliedCoupon(false);
        setCouponPricing(null);
        setCouponMessage(null);
      }
    } catch (err: any) {
      setCouponError(
        t.coupon?.errors?.validationError || 'An error occurred while validating the coupon code.'
      );
      setAppliedCoupon(false);
      setCouponPricing(null);
      setCouponMessage(null);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    onRemoveCoupon();
    setCouponCode('');
    setAppliedCoupon(false);
    setCouponPricing(null);
    setCouponMessage(null);
    setCouponError(null);
  };

  return (
    <div className="mb-6 rounded-lg bg-white p-6 shadow-md">
      <h3 className="mb-4 text-lg font-semibold">{t.coupon?.title || 'Coupon Code'}</h3>
      <div className="space-y-4">
        {!appliedCoupon ? (
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700">
              {t.coupon?.haveCoupon || 'Have a coupon code?'}
            </label>
            <div className="flex space-x-2">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder={t.coupon?.placeholder || 'Enter coupon code'}
                className={`flex-1 rounded-md border px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500 ${
                  couponError ? 'border-red-300' : 'border-gray-300'
                }`}
                disabled={applyingCoupon}
              />
              <button
                onClick={handleApplyCoupon}
                disabled={applyingCoupon || !couponCode.trim()}
                className={`rounded-md px-4 py-2 font-medium ${
                  applyingCoupon || !couponCode.trim()
                    ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                    : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
              >
                {applyingCoupon ? t.booking.applying : t.booking.apply}
              </button>
            </div>
            {couponError && <p className="mt-1 text-sm text-red-600">{couponError}</p>}
          </div>
        ) : (
          <div className="rounded-md border border-green-200 bg-green-50 p-4">
            <div className="mb-3 flex items-start justify-between">
              <div>
                <p className="font-medium text-green-800">
                  {t.coupon?.applied || 'Coupon applied!'}
                </p>
                {couponPricing && (
                  <div className="mt-1 text-sm text-green-700">
                    {/* Show sessions already used if applicable */}
                    {couponPricing.sessionsAlreadyUsed !== undefined &&
                      couponPricing.sessionsAlreadyUsed > 0 && (
                        <p className="text-gray-600">
                          {t.coupon?.sessionsAlreadyUsed?.replace(
                            '{{count}}',
                            String(couponPricing.sessionsAlreadyUsed)
                          ) ||
                            `${couponPricing.sessionsAlreadyUsed} session${couponPricing.sessionsAlreadyUsed > 1 ? 's' : ''} already used`}
                        </p>
                      )}
                    {/* Show remaining sessions on coupon */}
                    {couponPricing.sessionsRemainingOnCoupon !== undefined &&
                      couponPricing.sessionsAlreadyUsed === 0 &&
                      couponPricing.sessionsToApplyDiscount === 1 && (
                        <p className="text-blue-600">
                          {t.coupon?.bookAnotherLater ||
                            'Book another session later to use your free session!'}
                        </p>
                      )}
                    <div className="mt-1 text-green-700">
                      <p className="text-md mt-2 text-green-800">
                        {t.checkout?.subtotal}: {formatPrice(totalPrice)}
                      </p>
                      <p className="mt-1 text-lg font-bold">
                        {t.checkout?.total}: {formatPrice(effectivePrice)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <button
                onClick={handleRemoveCoupon}
                className="text-sm font-medium text-red-600 hover:text-red-800"
              >
                {t.booking.remove || 'Remove'}
              </button>
            </div>
            {couponMessage && (
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm text-blue-800">{couponMessage}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default CouponCode;
